import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import { fetchEvents } from "../services/api";
import "./Home.css";

const stats = [
  { number: "2+", label: "Mandal Connected" },
  { number: "0", label: "Devotees Reached" },
  { number: "0", label: "Donations Collected" },
  { number: "0", label: "Social Activities" },
];

const features = [
  {
    icon: "🏛️",
    title: "Mandal Directory",
    desc: "Find and connect with every registered Ganpati Mahotsav Mandal across Valsad district in one place.",
    link: "/mandals",
  },
  {
    icon: "💝",
    title: "Make a Donation",
    desc: "Contribute to community celebrations and social causes. Every donation, big or small, creates an impact.",
    link: "/donate",
  },
  {
    icon: "🤝",
    title: "Social Activities",
    desc: "Join hands for blood donation drives, cleanliness campaigns, tree plantations & community welfare.",
    link: "/events",
  },
  {
    icon: "📋",
    title: "Register Your Mandal",
    desc: "Officially register your Ganpati Mahotsav Mandal and become part of our growing network.",
    link: "/register",
  },
];

const SHOW_TESTIMONIALS = false;

const rewardHighlights = [
  {
    icon: "🏆",
    title: "Certificate of Appreciation",
    desc: "Mandal recognised for outstanding social welfare and community service will receive an official certificate of appreciation.",
  },
  {
    icon: "🎖️",
    title: "Mementos & Recognition",
    desc: "Top-performing mandal will be felicitated with mementos and public recognition on the Aarambh platform.",
  },
  {
    icon: "🌱",
    title: "Eco-Friendly Excellence",
    desc: "Mandal adopting eco-friendly practices — clay idols, natural colours, plastic-free pandals — will be specially honoured.",
  },
];

const testimonials = [
  {
    name: "Ramesh Patel",
    mandal: "Shri Siddhivinayak Mandal, Valsad",
    text: "Aarambh has made it incredibly easy for us to connect with other mandal, coordinate volunteers and receive donations. A blessing for our community!",
    avatar: "🙏",
  },
  {
    name: "Priya Desai",
    mandal: "Ganesh Utsav Mandal, Pardi",
    text: "The registration process was smooth and the platform helps us reach many more devotees. Truly a wonderful initiative for Valsad.",
    avatar: "🌸",
  },
  {
    name: "Mahesh Shah",
    mandal: "Jai Ganesh Mahotsav, Bulsar",
    text: "We have been able to organise several social activities through Aarambh. The support from this platform is commendable.",
    avatar: "⭐",
  },
];

export default function Home() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchEvents()
      .then((data) => {
        if (cancelled) return;
        const upcoming = (data || [])
          .filter((e) => e.isUpcoming)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setUpcomingEvents(upcoming);
      })
      .catch(() => {
        if (!cancelled) setUpcomingEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg-pattern" />
        <div className="container hero-content">
          <div className="badge">🕉 Valsad District</div>
          <h1 className="hero-title">
            Uniting the celebration <span className="hero-highlight">of Ganesh mahotsav with a purpose</span>
          </h1>
          <p className="hero-subtitle">
            Aarambh is an online platform dedicated to transforming Sarvajanik Ganesh Mahotsav into a movement for
            environmental protection, social welfare, and community unity, while making Ganesh Pandal registration
            simple, transparent, and seamless.
          </p>
          <div className="hero-actions">
            <Link to="/register" className="btn-primary">
              Register Mandal 📋
            </Link>
            <Link to="/donate" className="btn-outline">
              Donate Now 💝
            </Link>
            <Link to="/mandals" className="btn-outline">
              View All Mandals 🕉
            </Link>
          </div>

          <div className="hero-stats">
            {stats.map((s) => (
              <div key={s.label} className="hero-stat">
                <span className="stat-num">{s.number}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-ornament">
          <div className="om-circle">
            <span className="om-symbol">🕉</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <div className="badge">What We Do</div>
            <h2>One Platform, Many Connections</h2>
            <div className="divider">
              <div className="divider-line" />
              <span className="divider-icon">🌸</span>
              <div className="divider-line right" />
            </div>
            <p>
              Aarambh brings together devotion, community participation, environmental responsibility, and social
              service on one platform.
            </p>
          </div>

          <div className="features-grid">
            {features.map((f) => (
              <Link to={f.link} key={f.title} className="feature-card card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
                <span className="feature-link">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Strip */}
      <section className="about-strip">
        <div className="container about-strip-inner">
          <div className="about-strip-text">
            <div className="badge">Our Mission</div>
            <h2>Born from Devotion, Built for Community</h2>
            <p>
              Aarambh (आरंभ — meaning "The Beginning") was founded with a single vision: to unite every Ganpati Mahotsav
              Mandal in Valsad district so that together, we can celebrate Ganesh Utsav with greater joy, co-ordinate
              social activities and serve our community in Lord Ganesha's name.
            </p>
            <p>
              We believe that when mandal work together — sharing resources, volunteers and ideas — our celebrations
              become grander and our social impact becomes deeper.
            </p>
            <Link to="/about" className="btn-primary" style={{ marginTop: "16px", display: "inline-block" }}>
              Read Our Story
            </Link>
          </div>
          <div className="about-strip-visual">
            <div className="ganesha-card">
              <div className="ganesha-emoji">
                <img src={logo} alt="Aarambh" style={{ height: "80px", objectFit: "contain" }} />
              </div>
              <blockquote>
                "Vakratunda Mahakaya, Surya Koti Sama Prabha — Nirvighnam Kuru Me Deva, Sarva Karyeshu Sarvada"
              </blockquote>
              <p className="shloka-translate">
                O Lord Ganesha, remove all obstacles from our path and bless every endeavour.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="events-section">
        <div className="container">
          <div className="section-header">
            <div className="badge">Upcoming</div>
            <h2>Events & Activities</h2>
            <div className="divider">
              <div className="divider-line" />
              <span className="divider-icon">🌸</span>
              <div className="divider-line right" />
            </div>
          </div>

          <div className="events-list">
            {eventsLoading ? (
              <p style={{ textAlign: "center", color: "var(--text-light)" }}>Loading events…</p>
            ) : upcomingEvents.length === 0 ? (
              <p style={{ textAlign: "center", color: "var(--text-light)" }}>
                No upcoming events right now — check back soon.
              </p>
            ) : (
              upcomingEvents.map((ev) => {
                const d = new Date(ev.date);
                const day = d.toLocaleDateString("en-IN", { day: "2-digit" });
                const month = d.toLocaleDateString("en-IN", { month: "short" });
                return (
                  <div key={ev.id} className="event-row card">
                    <div className="event-date">
                      <span className="event-day">{day}</span>
                      <span className="event-month">{month}</span>
                    </div>
                    <div className="event-info">
                      {ev.category && <span className="event-badge">{ev.category}</span>}
                      <h3>{ev.title}</h3>
                      {ev.location && <p>📍 {ev.location}</p>}
                    </div>
                    <Link to={`/events/${ev.id}`} className="btn-outline" style={{ flexShrink: 0 }}>
                      View Details
                    </Link>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ textAlign: "center", marginTop: "40px" }}>
            <Link to="/events" className="btn-primary">
              See All Events
            </Link>
          </div>
        </div>
      </section>

      {/* Recognition & Rewards */}
      <section className="rewards-section">
        <div className="container">
          <div className="section-header">
            <div className="badge">Recognising Excellence</div>
            <h2>Great Mandal, Great Rewards</h2>
            <div className="divider">
              <div className="divider-line" />
              <span className="divider-icon">🌸</span>
              <div className="divider-line right" />
            </div>
            <p>
              Mandal that lead the way in social welfare and eco-friendly celebrations won't go unnoticed — they
              will be rewarded with certificates and mementos in recognition of their contribution.
            </p>
          </div>

          <div className="rewards-grid">
            {rewardHighlights.map((r) => (
              <div key={r.title} className="feature-card card">
                <div className="feature-icon">{r.icon}</div>
                <h3>{r.title}</h3>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donate CTA */}
      <section className="donate-cta">
        <div className="container donate-cta-inner">
          <div className="donate-cta-text">
            <h2>Your Donation Changes Lives</h2>
            <p>
              Support Ganesh Utsav celebrations, fund social activities and help underprivileged families in Valsad
              through your generous contribution.
            </p>
            <div className="donate-amounts">
              {["₹101", "₹501", "₹1,001", "₹5,001"].map((amt) => (
                <Link key={amt} to="/donate" className="amount-chip">
                  {amt}
                </Link>
              ))}
            </div>
          </div>
          <div className="donate-cta-action">
            <Link to="/donate" className="donate-big-btn">
              <span>🙏</span>
              <span>Donate Now</span>
            </Link>
            <p className="donate-note">100% transparent • All donations acknowledged</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {SHOW_TESTIMONIALS && (
        <section className="testimonials-section">
          <div className="container">
            <div className="section-header">
              <div className="badge">Community Voices</div>
              <h2>What Our Members Say</h2>
              <div className="divider">
                <div className="divider-line" />
                <span className="divider-icon">🌸</span>
                <div className="divider-line right" />
              </div>
            </div>

            <div className="testimonials-grid">
              {testimonials.map((t) => (
                <div key={t.name} className="testimonial-card card">
                  <div className="testimonial-avatar">{t.avatar}</div>
                  <p className="testimonial-text">"{t.text}"</p>
                  <div className="testimonial-author">
                    <strong>{t.name}</strong>
                    <span>{t.mandal}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Register CTA */}
      <section className="register-cta">
        <div className="container" style={{ textAlign: "center" }}>
          <div className="badge">Join the Family</div>
          <h2>Register Your Mandal Today</h2>
          <p>
            Is your Ganpati Mahotsav Mandal based in Valsad? Get listed on our directory, receive donations and
            coordinate with other mandal — completely free.
          </p>
          <div style={{ display: "flex", gap: "16px", justifyContent: "center", marginTop: "28px", flexWrap: "wrap" }}>
            <Link to="/register" className="btn-primary">
              Register Now
            </Link>
            <Link to="/contact" className="btn-outline">
              Ask a Question
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
